import { useEffect, useRef, useState } from "react";
import type { ResourceResponse } from "types";
import { useSimpleRequest } from "hooks";
import { when } from "util/support";
import { Filters } from "consts/onboarding";
import { useParams } from "infrastructure/router";
import { WithOnboardingStep } from "features/onboarding/types";
import type { ApplicationDetailProviderProps, WithStep } from "./types";

type Step<T extends object = {}> = WithStep<{
    readonly isLoading: boolean;
    readonly response: ResourceResponse<
        WithOnboardingStep<T>
    >;
}>;

export const useApplicationDetail = ({ steps }: ApplicationDetailProviderProps) => {
    const params = useParams();
    const pki = params[Filters.pki];

    const [isLoading, setLoading] = useState(false);
    const [state, setState] = useState<Map<string, Step>>(() => new Map());
    const stateRef = useRef(state);
    stateRef.current = state;

    const updateStep = (step: string, state: Partial<Step>) => {
        stateRef.current.set(step, {
            ...stateRef.current.get(step)!,
            ...state
        });
        setState(new Map(stateRef.current));
    };

    const request = useSimpleRequest();

    const featchAll = async () => {
        setLoading(true);
        const state: Map<string, Step> = new Map();

        for (const [step, { response, ...rest }] of Array.from(
            Array.from(steps.entries())
                .reduce((map, [step, { fetcher, ...rest }]) => {
                    const response: Promise<ResourceResponse<WithOnboardingStep<object>>> = request(() => fetcher({
                        userId: `${pki}`
                    }));

                    return map.set(step, {
                        ...rest,
                        response,
                    });
                }, new Map<string, WithStep<{
                    readonly response: Promise<ResourceResponse<WithOnboardingStep<object>>>
                }>>())
                .entries()
        )) {
            state.set(step, {
                ...rest,
                isLoading: false,
                response: await response
            });
        }

        setState(state);
        setLoading(false);
    };

    const fetchAllRef = useRef(featchAll);
    fetchAllRef.current = featchAll;

    const fetchStep = async (step: string) => {
        const { fetcher } = steps.get(step)!;

        updateStep(step, {
            isLoading: true
        });

        const response = await request(() => fetcher({
            userId: `${pki}`
        }));

        updateStep(step, {
            isLoading: false,
            response
        });
    };

    const getState = () => stateRef.current;

    const getStep = <T extends object>(step: string) => getState().get(step) as Step<T>;

    const getTotalStepFields = (step: string) => getStep(step)?.totalStepFields ?? 0;

    const getStepData = <T extends object>(step: string) => getStep<T>(step)?.response.data ?? {};

    const getTotalStepsCount = () => steps.size;

    useEffect(() => {
        when(pki, fetchAllRef.current);
    }, [pki]);

    return {
        isLoading,
        getState,
        getStep,
        getTotalStepFields,
        getStepData,
        featchAll,
        fetchStep,
        getTotalStepsCount
    };
};

export default useApplicationDetail;
